import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Phone, Search, Building2, Siren, HeartPulse, TreePine,
  Edit, Save, Plus, Trash2, X, GripVertical
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config/constants";

const ICON_MAP = { Siren, Building2, HeartPulse, TreePine, Phone };
const ICON_OPTIONS = ["Siren", "Building2", "HeartPulse", "TreePine", "Phone"];
const COLOR_MAP = {
  Siren:      { color: "text-red-500",     bg: "bg-red-500/10",     border: "border-red-500/20" },
  Building2:  { color: "text-blue-500",    bg: "bg-blue-500/10",    border: "border-blue-500/20" },
  HeartPulse: { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  TreePine:   { color: "text-amber-500",   bg: "bg-amber-500/10",   border: "border-amber-500/20" },
  Phone:      { color: "text-violet-500",  bg: "bg-violet-500/10",  border: "border-violet-500/20" },
};

function toTelHref(number) {
  const base = number.replace(/ext\s*/i, ",").replace(/[^\d+,]/g, "");
  return `tel:${base}`;
}

export default function PhoneBook() {
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isAdmin, isEditor, token } = useAuth();
  const canEdit = isAdmin || isEditor;

  useEffect(() => {
    axios.get(`${API}/phonebook`).then((res) => {
      setGroups(res.data);
    }).catch(() => {
      toast.error("Failed to load phone book");
    }).finally(() => setLoading(false));
  }, []);

  const startEdit = () => {
    setEditData(JSON.parse(JSON.stringify(groups)));
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setEditData([]); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/phonebook`, editData, { headers: { Authorization: `Bearer ${token}` } });
      setGroups(JSON.parse(JSON.stringify(editData)));
      setEditing(false);
      toast.success("Phone book updated");
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  // Edit helpers
  const updateGroup = (gi, field, val) => {
    const d = [...editData]; d[gi] = { ...d[gi], [field]: val }; setEditData(d);
  };
  const addGroup = () => {
    setEditData([...editData, { category: "New Category", icon: "Phone", contacts: [] }]);
  };
  const removeGroup = (gi) => {
    setEditData(editData.filter((_, i) => i !== gi));
  };
  const updateContact = (gi, ci, field, val) => {
    const d = [...editData];
    const contacts = [...d[gi].contacts];
    contacts[ci] = { ...contacts[ci], [field]: val };
    d[gi] = { ...d[gi], contacts };
    setEditData(d);
  };
  const addContact = (gi) => {
    const d = [...editData];
    d[gi] = { ...d[gi], contacts: [...d[gi].contacts, { name: "", number: "" }] };
    setEditData(d);
  };
  const removeContact = (gi, ci) => {
    const d = [...editData];
    d[gi] = { ...d[gi], contacts: d[gi].contacts.filter((_, i) => i !== ci) };
    setEditData(d);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.map((g) => ({
      ...g,
      contacts: g.contacts.filter(
        (c) => c.name.toLowerCase().includes(q) || c.number.includes(q) || g.category.toLowerCase().includes(q)
      ),
    })).filter((g) => g.contacts.length > 0);
  }, [search, groups]);

  const totalContacts = groups.reduce((s, g) => s + g.contacts.length, 0);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;

  // EDIT MODE
  if (editing) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="phone-book-edit">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-primary" />
            </div>
            Edit Phone Book
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={cancelEdit}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="phonebook-save-btn">
              <Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {editData.map((group, gi) => (
          <Card key={gi} data-testid={`edit-group-${gi}`}>
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center gap-2">
                <select
                  value={group.icon}
                  onChange={(e) => updateGroup(gi, "icon", e.target.value)}
                  className="h-8 rounded border bg-background px-2 text-sm"
                >
                  {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <Input
                  value={group.category}
                  onChange={(e) => updateGroup(gi, "category", e.target.value)}
                  className="font-semibold flex-1"
                  placeholder="Category name"
                />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removeGroup(gi)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-3 space-y-1.5">
              {group.contacts.map((c, ci) => (
                <div key={ci} className="flex items-center gap-2 group">
                  <Input
                    value={c.name}
                    onChange={(e) => updateContact(gi, ci, "name", e.target.value)}
                    placeholder="Contact name"
                    className="flex-1 h-8 text-sm"
                  />
                  <Input
                    value={c.number}
                    onChange={(e) => updateContact(gi, ci, "number", e.target.value)}
                    placeholder="Phone number"
                    className="w-52 h-8 text-sm font-mono"
                  />
                  <button
                    className="h-6 w-6 flex items-center justify-center rounded hover:bg-destructive/10 text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={() => removeContact(gi, ci)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="text-xs mt-1" onClick={() => addContact(gi)}>
                <Plus className="w-3 h-3 mr-1" />Add Contact
              </Button>
            </CardContent>
          </Card>
        ))}

        <Button variant="outline" onClick={addGroup} className="w-full">
          <Plus className="w-4 h-4 mr-1" />Add Category
        </Button>
      </div>
    );
  }

  // VIEW MODE
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="phone-book">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold flex items-center gap-3" data-testid="phone-book-title">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-primary" />
            </div>
            Phone Book
          </h1>
          <p className="text-muted-foreground mt-1 ml-[52px]">
            {totalContacts} contacts &mdash; tap to call
          </p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="phonebook-edit-btn">
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="phone-book-search"
        />
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-muted-foreground py-12" data-testid="phone-book-empty">
          No contacts match "{search}"
        </p>
      )}

      {filtered.map((group) => {
        const iconKey = group.icon || "Phone";
        const Icon = ICON_MAP[iconKey] || Phone;
        const colors = COLOR_MAP[iconKey] || COLOR_MAP.Phone;
        return (
          <Card key={group.category} className={colors.border} data-testid={`phone-group-${group.category.replace(/\s+/g, "-").toLowerCase()}`}>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-base font-heading">
                <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4 h-4 ${colors.color}`} />
                </div>
                {group.category}
                <Badge variant="outline" className="ml-auto font-normal text-xs">
                  {group.contacts.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="divide-y divide-border">
                {group.contacts.map((c) => (
                  <a
                    key={c.name}
                    href={toTelHref(c.number)}
                    className="flex items-center justify-between py-2.5 hover:bg-accent/50 -mx-2 px-2 rounded-md transition-colors group"
                    data-testid={`phone-contact-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <span className="text-sm font-medium">{c.name}</span>
                    <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1.5 shrink-0 ml-4">
                      <Phone className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {c.number}
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
